import { prettifyCategory } from './categoryLabel';

// 6D Freshness gap label — pluralize the "day(s) behind" suffix (markup #57).
export function formatDaysBehind(days) {
  if (days == null || days === '') return '';
  const n = Number(days);
  if (!Number.isFinite(n)) return '';
  const word = n === 1 ? 'day' : 'days';
  return `${n} ${word} behind`;
}

// 6E Category Confusion donut — map the confusion index (0..1) to its tier
// label rendered in the donut center (markup #59).
export function confusionTier(ci) {
  if (ci == null) return null;
  const n = Number(ci);
  if (!Number.isFinite(n)) return null;
  if (n < 0.34) return 'Low Confusion';
  if (n < 0.67) return 'Moderate';
  return 'High Confusion';
}

/**
 * Which condition made a 6E ground truth read as stale.
 *
 * `ground_truth_property_type_stale` is a single boolean over three separate
 * conditions, which the panel used to report as one catch-all "(uncurated)".
 * `fn_get_aim_category_confusion` now returns the components, so the panel can
 * name the one that actually fired — the three call for different fixes.
 *
 * Checked in order of severity: nothing recorded beats a disagreement, which
 * beats mere age.
 *
 * @param {object|null|undefined} data the `fn_get_aim_category_confusion` payload
 * @returns {{reason: 'missing'|'disputed'|'aged', label: string, ageDays: number|null}|null}
 *   `null` when the payload does not report the fact as stale.
 */
export function categoryConfusionStaleness(data) {
  if (data?.ground_truth_property_type_stale !== true) return null;
  const rawAge = Number(data?.ground_truth_age_days);
  const ageDays = Number.isFinite(rawAge) ? rawAge : null;

  if (data?.ground_truth_missing === true) {
    return { reason: 'missing', label: 'not recorded', ageDays };
  }
  if (data?.matches_ground_truth === false) {
    return { reason: 'disputed', label: 'disputed by AI', ageDays };
  }
  return {
    reason: 'aged',
    label: ageDays != null ? `${ageDays} day${ageDays === 1 ? '' : 's'} old` : 'uncurated',
    ageDays,
  };
}

/**
 * Tooltip copy for a 6E staleness marker.
 *
 * Names the condition that fired and, separately, how old the recorded fact is
 * — the two are independent signals and the age is the actionable number, so a
 * disputed fact still reports its age rather than only the disagreement. The
 * age sentence is omitted entirely when the payload reports no age, rather than
 * printing "null days".
 *
 * @param {{reason: string, ageDays: number|null}|null|undefined} staleness from `categoryConfusionStaleness`
 * @param {object|null|undefined} data the `fn_get_aim_category_confusion` payload
 * @returns {string|null} tooltip text, or null when nothing is stale
 */
export function categoryConfusionStaleDetail(staleness, data) {
  if (!staleness) return null;
  const dominant = data?.dominant_category ? prettifyCategory(data.dominant_category) : null;
  const pct = Number(data?.dominant_category_pct);

  let cause;
  if (staleness.reason === 'missing') {
    cause = 'No property type is recorded for you, so AI\'s answers have nothing to be checked against.';
  } else if (staleness.reason === 'disputed') {
    cause = dominant && Number.isFinite(pct)
      ? `AI most often calls you ${dominant} (${pct.toFixed(1)}%), which disagrees with the type you recorded.`
      : 'AI\'s dominant category disagrees with the type you recorded.';
  } else {
    cause = 'Your recorded property type has not been re-confirmed recently.';
  }

  const age = staleness.ageDays != null
    ? ` It was last confirmed ${staleness.ageDays} day${staleness.ageDays === 1 ? '' : 's'} ago.`
    : '';
  return `${cause}${age} Confirm it in the §6A grid so AI scoring stays current.`;
}

/**
 * Split a 6C row's providers into the four mutually exclusive buckets the
 * Providers bar renders.
 *
 * **Do not rebuild this from `providers_agreeing` / `providers_disagreeing`.**
 * Those two fields are *overlapping sets*, not a partition: `providers_agreeing`
 * counts every provider with at least one accurate statement and
 * `providers_disagreeing` every provider with at least one inaccurate one, so a
 * provider that is right about one statement and wrong about another is counted
 * in **both**. Their sum therefore exceeds the number of providers that stated
 * the fact — on 7 of the 56 rows in the 2026-08-10 capture it does — and a bar
 * normalised over that sum contradicts the Consensus % column in its own row
 * (`distance_to_sedona` drew 8/(8+6) = 57.1% beside a column reading 100%).
 * That defect is why the bar was deleted in `98c0323`; two reviewers found it
 * independently.
 *
 * The fix is to go back to the grain the RPC actually ships. `providers[]` holds
 * one entry per *statement*, so a provider appears more than once. Grouping the
 * statements by provider and classifying each provider once gives buckets that
 * are mutually exclusive by construction — they sum to `providers_stating` on
 * all 54 rows of the capture that carry statements, so the bar cannot exceed
 * 100%, and `(agreed + mixed) / stating` reproduces `consensus_pct` on 53 of the
 * 54. The one exception, `tripadvisor_award`, is ungraded: its unscored provider
 * lands in `unscored` while the backend drops it from the column's denominator.
 * It is deliberately not special-cased — a bar showing half its providers
 * unscored is the honest reading of an ungraded row.
 *
 * `is_accurate` is tri-state (`true` / `false` / `null`); only an explicit
 * `false` is a disagreement, so a provider whose statements were never scored is
 * `unscored` rather than silently counted as agreeing.
 *
 * @param {object|null|undefined} row one consensus row
 * @returns {{agreed: number, mixed: number, disagreed: number, unscored: number,
 *   stating: number}|null} the partition, or `null` when the row carries no
 *   statements — no statements means the fact was not measured, which the caller
 *   must render as a dash rather than as an empty or all-zero bar.
 */
export function consensusProviderPartition(row) {
  const statements = Array.isArray(row?.providers) ? row.providers : [];
  if (statements.length === 0) return null;

  const byProvider = new Map();
  for (const s of statements) {
    const name = s?.provider ?? s?.display_name;
    if (!name) continue;
    if (!byProvider.has(name)) byProvider.set(name, { accurate: false, inaccurate: false });
    const seen = byProvider.get(name);
    if (s.is_accurate === true) seen.accurate = true;
    if (s.is_accurate === false) seen.inaccurate = true;
  }
  if (byProvider.size === 0) return null;

  const partition = { agreed: 0, mixed: 0, disagreed: 0, unscored: 0, stating: byProvider.size };
  for (const { accurate, inaccurate } of byProvider.values()) {
    if (accurate && inaccurate) partition.mixed += 1;
    else if (accurate) partition.agreed += 1;
    else if (inaccurate) partition.disagreed += 1;
    else partition.unscored += 1;
  }
  return partition;
}

/** How each bucket names itself inside the partition sentence. */
const PARTITION_PHRASE = {
  agreed: (n) => `${n} agreed`,
  mixed: (n) => `${n} gave conflicting answers`,
  disagreed: (n) => `${n} disagreed`,
  unscored: (n) => `${n} ${n === 1 ? 'was' : 'were'} unscored`,
};

/**
 * The accessible sentence describing a 6C provider partition.
 *
 * The label this replaces read "8 accurate, 6 inaccurate, 0 fabricated, 0 silent
 * of 8 providers" — 14 things out of 8, because it was built from the
 * overlapping counts (see `consensusProviderPartition`). The buckets here are
 * exclusive, so the sentence can state counts of a total honestly. Buckets
 * holding nobody are left out rather than read aloud as a row of zeroes.
 *
 * @param {{agreed: number, mixed: number, disagreed: number, unscored: number,
 *   stating: number}|null|undefined} partition from `consensusProviderPartition`
 * @returns {string|null} the sentence, or `null` when no provider stated the fact
 */
export function consensusPartitionLabel(partition) {
  if (!partition || !partition.stating) return null;
  const named = Object.keys(PARTITION_PHRASE)
    .filter((bucket) => partition[bucket] > 0)
    .map((bucket) => PARTITION_PHRASE[bucket](partition[bucket]));
  const list = named.length > 1
    ? `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`
    : named[0];
  const providers = `${partition.stating} provider${partition.stating === 1 ? '' : 's'}`;
  return `${providers} stated this fact: ${list}.`;
}

/**
 * The providers that got a 6C fact wrong, and what they said.
 *
 * `fn_get_aim_consensus_detail` ships a `providers[]` array of every statement
 * made about the fact — one entry per statement, so a provider appears more
 * than once. Both lists are de-duplicated and stable-sorted by first appearance.
 *
 * A row with no `providers[]` yields empty lists rather than a breakdown. The
 * Express query this replaced shipped pre-flattened `wrong_providers` /
 * `wrong_values` arrays, which used to be read as a fallback; that was dropped
 * on 2026-08-11 because nothing can produce them — `getAimConsensusDetail`
 * calls only `fn_get_aim_consensus_detail`, and none of the 56 rows it returns
 * carries either key. The Providers cell could not render that shape anyway,
 * so the fallback made the module look legacy-compatible when it was not.
 *
 * `is_accurate` is tri-state: `true`, `false`, or `null` for a statement nobody
 * scored. Only an explicit `false` is a disagreement — `!null` would report an
 * unscored statement as wrong, which is how `tripadvisor_award` came to expand
 * into "Wrong: Perplexity" under a 100%-consensus bar.
 *
 * @param {object|null|undefined} row one consensus row
 * @returns {{providers: string[], values: string[]}}
 */
export function consensusDisagreements(row) {
  const stated = Array.isArray(row?.providers) ? row.providers : null;
  if (!stated) return { providers: [], values: [] };
  const wrong = stated.filter((p) => p?.is_accurate === false);
  return {
    providers: [...new Set(wrong.map((p) => p.provider).filter(Boolean))],
    values: [...new Set(wrong.map((p) => p.stated).filter(Boolean))],
  };
}
